import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import GSTSelector from "./GSTSelector";
import Modal from "./Modal";
import { clearAuthData } from "../utils/auth";
import { AUTH_EXPIRED_EVENT } from "../utils/sessionIdle";
import { useSessionIdleMonitor } from "../utils/useSessionIdleMonitor";
import { publicSettingsAPI } from "../api/adminMonitoring";
import { colors } from "../styles/commonStyles";
import { setSelectedGSTAndBroadcast } from "../lib/gstSelection";
import { safeGet, safeRemove, safeGetJSON } from "../utils/safeStorage";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { hasActiveUnsavedNavigationGuard, tryNavigateAway } from "../lib/unsavedNavigationGuard";
import { useMyPermissions } from "../hooks/useMyPermissions";

const MODAL_OPEN_EVENT = "exportnook:modal-open";
const MODAL_CLOSE_EVENT = "exportnook:modal-close";

// Below this width the sidebar becomes a drawer with a backdrop, and the
// main content reclaims the full width. Keep this aligned with the
// breakpoint in src/index.css.
const SIDEBAR_DRAWER_BREAKPOINT = 900;

const SIDEBAR_LINK_CLASS = (active) =>
  active ? "layout-nav-item layout-nav-item--active" : "layout-nav-item";

const getIsNarrow = () =>
  typeof window !== "undefined" &&
  window.matchMedia(`(max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px)`).matches;

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useMyPermissions();
  const profileMenuRef = useRef(null);
  const layoutRootRef = useRef(null);
  const openModalIdsRef = useRef(new Set());
  const [user, setUser] = useState(null);
  const [selectedGST, setSelectedGST] = useState(null);
  const [browserExtensionEnabled, setBrowserExtensionEnabled] = useState(false);
  const [reconciliationIssuesEnabled, setReconciliationIssuesEnabled] = useState(false);
  const [awbCreateEnabled, setAwbCreateEnabled] = useState(false);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [extensionChecking, setExtensionChecking] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(getIsNarrow);
  // Sidebar is closed by default on narrow viewports (drawer), open on desktop.
  const [sidebarOpen, setSidebarOpen] = useState(() => !getIsNarrow());
  const [anyModalOpen, setAnyModalOpen] = useState(false);
  const [authExpiredModalOpen, setAuthExpiredModalOpen] = useState(false);

  useSessionIdleMonitor();

  // Track viewport to switch between docked sidebar and drawer.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(
      `(max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px)`
    );
    const handleChange = (e) => {
      setIsNarrow(e.matches);
      // When crossing the threshold, auto-close the drawer on narrow and
      // auto-open the docked sidebar on desktop for a sensible default.
      setSidebarOpen(!e.matches);
    };
    // `addEventListener` is the modern API; `addListener` is the legacy
    // fallback for older Safari.
    if (mq.addEventListener) mq.addEventListener("change", handleChange);
    else mq.addListener(handleChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handleChange);
      else mq.removeListener(handleChange);
    };
  }, []);

  // Global auth-expiry UX: do not hard-redirect on 401; show a modal instead.
  useEffect(() => {
    const onExpired = () => setAuthExpiredModalOpen(true);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const handleAuthLogin = useCallback(() => {
    // Clear auth so ProtectedRoute can hand-off to login cleanly.
    try {
      clearAuthData();
    } catch {}
    setAuthExpiredModalOpen(false);
    navigate("/login", {
      replace: true,
      state: { from: location, message: "Session expired. Please login to continue." },
    });
  }, [navigate, location]);

  // On narrow viewports, auto-close the drawer when the route changes.
  useEffect(() => {
    if (isNarrow) setSidebarOpen(false);
  }, [location.pathname, isNarrow]);

  // UI policy: whenever any modal opens, auto-close the sidebar menu
  useEffect(() => {
    const handleModalOpen = (e) => {
      const id = e?.detail?.id;
      if (id) openModalIdsRef.current.add(id);
      setAnyModalOpen(true);
      setSidebarOpen(false);
    };

    const handleModalClose = (e) => {
      const id = e?.detail?.id;
      if (id) openModalIdsRef.current.delete(id);
      setAnyModalOpen(openModalIdsRef.current.size > 0);
      // Intentionally do not re-open the sidebar on modal close.
    };

    window.addEventListener(MODAL_OPEN_EVENT, handleModalOpen);
    window.addEventListener(MODAL_CLOSE_EVENT, handleModalClose);
    return () => {
      window.removeEventListener(MODAL_OPEN_EVENT, handleModalOpen);
      window.removeEventListener(MODAL_CLOSE_EVENT, handleModalClose);
    };
  }, []);

  useEffect(() => {
    const userData = safeGetJSON(STORAGE_KEYS.user);
    if (userData) setUser(userData);

    const gstData = safeGetJSON(STORAGE_KEYS.selectedGST);
    if (gstData) {
      setSelectedGST(gstData);
    } else if (safeGet(STORAGE_KEYS.selectedGST)) {
      // Corrupted JSON; clear it so we stop retrying.
      safeRemove(STORAGE_KEYS.selectedGST);
    }

    // Load page visibility settings
    const loadPageSettings = async () => {
      try {
        const [browserExtensionData, reconciliationIssuesData, awbCreateData] = await Promise.all([
          publicSettingsAPI.getBrowserExtensionStatus(),
          publicSettingsAPI.getReconciliationIssuesStatus(),
          publicSettingsAPI.getAWBCreateStatus()
        ]);
        console.log('Loading page settings:', {
          browserExtension: browserExtensionData.enabled,
          reconciliationIssues: reconciliationIssuesData.enabled,
          awbCreate: awbCreateData.enabled
        });
        setBrowserExtensionEnabled(browserExtensionData.enabled);
        setReconciliationIssuesEnabled(reconciliationIssuesData.enabled);
        setAwbCreateEnabled(awbCreateData.enabled);
      } catch (error) {
        console.error('Error loading page settings:', error);
        // Default to disabled if API fails (safer default)
        setBrowserExtensionEnabled(false);
        setReconciliationIssuesEnabled(false);
        setAwbCreateEnabled(false);
      }
    };

    loadPageSettings();

    // Listen for settings changes from admin dashboard
    const handleSettingsChange = () => {
      console.log('Settings changed event received, reloading...');
      loadPageSettings();
    };

    window.addEventListener('systemSettingsChanged', handleSettingsChange);

    return () => {
      window.removeEventListener('systemSettingsChanged', handleSettingsChange);
    };
  }, []);

  // Listen for GST selection changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      const gstData = safeGetJSON(STORAGE_KEYS.selectedGST);
      if (gstData) setSelectedGST(gstData);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('gstSelectionChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('gstSelectionChanged', handleStorageChange);
    };
  }, []);

  const handleGSTChange = useCallback((gst) => {
    setSelectedGST(gst);
    setSelectedGSTAndBroadcast(gst);
  }, []);

  // Extension detection (PING/PONG) for top-bar indicator
  useEffect(() => {
    if (!browserExtensionEnabled) {
      setExtensionChecking(false);
      return;
    }
    let isDetected = false;
    const handleExtensionMessage = (e) => {
      if (e.data?.type === 'PONG_EXTENSION' || e.data?.type === 'EXTENSION_READY') {
        if (!isDetected) {
          isDetected = true;
          setExtensionInstalled(true);
          setExtensionChecking(false);
        }
      }
    };
    window.addEventListener('message', handleExtensionMessage);
    window.postMessage({ type: 'PING_EXTENSION' }, '*');
    const t1 = setTimeout(() => window.postMessage({ type: 'PING_EXTENSION' }, '*'), 100);
    const t2 = setTimeout(() => window.postMessage({ type: 'PING_EXTENSION' }, '*'), 500);
    const timeout = setTimeout(() => {
      if (!isDetected) {
        setExtensionInstalled(false);
        setExtensionChecking(false);
      }
      window.removeEventListener('message', handleExtensionMessage);
      clearTimeout(t1);
      clearTimeout(t2);
    }, 2000);
    return () => {
      window.removeEventListener('message', handleExtensionMessage);
      clearTimeout(timeout);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [browserExtensionEnabled]);

  // Click outside to close profile menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Intercept sidebar/topbar in-app links when a page registered an unsaved guard.
  useEffect(() => {
    const root = layoutRootRef.current;
    if (!root) return undefined;

    const onClickCapture = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const rawHref = anchor.getAttribute("href");
      if (!rawHref || !rawHref.startsWith("/")) return;

      let path;
      let search = "";
      let hash = "";
      try {
        const url = new URL(rawHref, window.location.origin);
        path = url.pathname;
        search = url.search;
        hash = url.hash;
      } catch {
        return;
      }

      if (path === location.pathname && search === location.search && hash === location.hash) {
        return;
      }

      if (!hasActiveUnsavedNavigationGuard()) return;

      e.preventDefault();
      e.stopPropagation();
      tryNavigateAway(() => navigate(`${path}${search}${hash}`));
    };

    root.addEventListener("click", onClickCapture, true);
    return () => root.removeEventListener("click", onClickCapture, true);
  }, [location.pathname, location.search, location.hash, navigate]);

  const isAdmin = user?.is_admin;
  const isSupport = user?.is_support;

  return (
    <div ref={layoutRootRef} style={dashboardLayout}>
      <style>{`
        [data-topbar-extension-link]:hover { background: rgba(255,255,255,0.1); }
        [data-profile-dropdown-item]:hover { background: #f3f4f6; }
        [data-topbar-profile-btn]:hover { background: #475569; }
        [data-topbar-logo]:hover { opacity: 0.9; }
        [data-topbar-menu-btn]:hover { background: rgba(255,255,255,0.1); }
      `}</style>

      <Modal
        open={authExpiredModalOpen}
        title="Session expired"
        onClose={() => setAuthExpiredModalOpen(false)}
        footer={
          <div className="modal-actions-row modal-actions-row--end modal-actions-row--stack-sm">
            <button
              type="button"
              onClick={() => setAuthExpiredModalOpen(false)}
              style={{
                background: "#fff",
                border: "1px solid #d1d5db",
                color: "#111827",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Stay on this page
            </button>
            <button
              type="button"
              onClick={handleAuthLogin}
              style={{
                background: "#3b82f6",
                border: "none",
                color: "#fff",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Login
            </button>
          </div>
        }
      >
        <div style={{ padding: "16px 0", color: "#374151", lineHeight: 1.6, fontSize: 14 }}>
          Your session is no longer valid. Please login again to continue.
        </div>
      </Modal>
      {/* Top bar – fixed, always visible */}
      <header className="layout-topbar" style={topBar}>
        <div className="layout-topbar-left" style={topBarLeft}>
          <button
            type="button"
            onClick={() => {
              if (anyModalOpen) return;
              setSidebarOpen((o) => !o);
            }}
            disabled={anyModalOpen}
            style={{
              ...topBarMenuBtn,
              opacity: anyModalOpen ? 0.5 : 1,
              cursor: anyModalOpen ? "not-allowed" : "pointer",
            }}
            title={sidebarOpen ? "Collapse menu" : "Show menu"}
            aria-label={sidebarOpen ? "Collapse menu" : "Show menu"}
            aria-disabled={anyModalOpen ? "true" : "false"}
            data-topbar-menu-btn
          >
            <FiMenu size={22} color="#f8fafc" />
          </button>
          <Link to="/dashboard" className="layout-topbar-logo" style={topBarLogo} data-topbar-logo>
            ExportNook
          </Link>
          <span className="layout-topbar-divider" style={topBarDivider} aria-hidden />
          <div className="layout-topbar-gst-wrap" style={topBarGSTWrap}>
            <GSTSelector
              onGSTChange={handleGSTChange}
              selectedGSTId={selectedGST?.value}
              variant="topbar"
            />
          </div>
        </div>
        <div className="layout-topbar-right" style={topBarRight}>
          {browserExtensionEnabled && (
            <Link
              to="/extensions"
              title={extensionChecking ? 'Checking…' : extensionInstalled ? 'Extension installed' : 'Extension not installed or not working'}
              className="layout-topbar-extension-link"
              style={topBarExtensionLink}
              data-topbar-extension-link
            >
              <span
                style={{
                  ...topBarExtensionDot,
                  backgroundColor: extensionChecking
                    ? colors.gray300
                    : extensionInstalled
                      ? colors.success
                      : colors.error
                }}
              />
              <span className="layout-topbar-extension-label hide-mobile" style={topBarExtensionLabel}>Extension</span>
            </Link>
          )}
          {browserExtensionEnabled && <span className="layout-topbar-divider hide-mobile" style={topBarDivider} aria-hidden />}
          <div style={{ position: 'relative' }} ref={profileMenuRef}>
            <button
              type="button"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              style={topBarProfileBtn}
              title="Profile menu"
              aria-expanded={profileMenuOpen}
              data-topbar-profile-btn
            >
              <span style={topBarProfileIcon}>
                {user?.email?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </button>
            {profileMenuOpen && (
              <div style={profileDropdown}>
                <button
                  type="button"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    tryNavigateAway(() => navigate("/profile"));
                  }}
                  style={profileDropdownItem}
                  data-profile-dropdown-item
                >
                  Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    clearAuthData();
                    window.location.href = '/login';
                  }}
                  style={{ ...profileDropdownItem, color: colors.error }}
                  data-profile-dropdown-item
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {user?.is_sub_user && (
        <div
          role="status"
          style={{
            padding: "6px 16px",
            backgroundColor: "#fef3c7",
            borderBottom: "1px solid #fbbf24",
            color: "#92400e",
            fontSize: 13,
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          Acting as sub-user <strong>{user.sub_user_display_name || user.sub_user_email}</strong> of {user.company_name}
        </div>
      )}

      <div style={mainRow}>
      {/* Backdrop for drawer mode (narrow viewports). */}
      {isNarrow && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          aria-hidden
          style={sidebarBackdrop}
        />
      )}
      {/* Side Menu – fixed; docked on desktop, drawer on narrow viewports. */}
      <aside
        className="layout-sidebar"
        style={{
          ...sideMenu,
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s ease",
          zIndex: isNarrow ? 110 : 90,
          boxShadow: isNarrow && sidebarOpen
            ? "2px 0 16px rgba(0,0,0,0.35)"
            : "2px 0 8px rgba(0,0,0,0.1)",
        }}
        aria-hidden={!sidebarOpen}
      >
        <nav className="layout-nav" style={navSection}>
          <div className="layout-nav-group" style={navGroup}>
            <Link 
              to="/dashboard" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/dashboard')}
              style={{
                ...navItem,
                ...(location.pathname === '/dashboard' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>🏠</span>
              Dashboard
            </Link>
          </div>

          <div className="layout-nav-group" style={navGroup}>
            <div className="layout-nav-group-title" style={navGroupTitle}>Invoices</div>
            {can('invoice.create') && (
              <Link
                to="/invoice/create"
                className={SIDEBAR_LINK_CLASS(location.pathname === '/invoice/create')}
                style={{
                  ...navItem,
                  ...(location.pathname === '/invoice/create' ? navItemActive : {})
                }}
              >
                <span className="layout-nav-icon" style={navIcon}>📄</span>
                Create Invoice
              </Link>
            )}
            <Link 
              to="/invoices" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/invoices')}
              style={{
                ...navItem,
                ...(location.pathname === '/invoices' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📋</span>
              View Invoices
            </Link>
            <Link
              to="/courier-reconciliation"
              className={SIDEBAR_LINK_CLASS(location.pathname === "/courier-reconciliation")}
              style={{
                ...navItem,
                ...(location.pathname === "/courier-reconciliation" ? navItemActive : {}),
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>🚚</span>
              Courier Reconciliation
            </Link>
            <Link 
              to="/export-documents" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/export-documents')}
              style={{
                ...navItem,
                ...(location.pathname === '/export-documents' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📑</span>
              Export Documents
            </Link>
          </div>
          
          <div className="layout-nav-group" style={navGroup}>
            <div className="layout-nav-group-title" style={navGroupTitle}>Documents</div>
            <Link 
              to="/packing-list/create" 
              className={SIDEBAR_LINK_CLASS(location.pathname.startsWith('/packing-list'))}
              style={{
                ...navItem,
                ...(location.pathname.startsWith('/packing-list') ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📦</span>
              Packing List
            </Link>
            {awbCreateEnabled && (
              <Link 
                to="/awb/create" 
                className={SIDEBAR_LINK_CLASS(location.pathname.startsWith('/awb'))}
                style={{
                  ...navItem,
                  ...(location.pathname.startsWith('/awb') ? navItemActive : {})
                }}
              >
                <span className="layout-nav-icon" style={navIcon}>🚚</span>
                Create AWB
              </Link>
            )}
            {reconciliationIssuesEnabled && (
              <Link 
                to="/reconciliation-issues" 
                className={SIDEBAR_LINK_CLASS(location.pathname === '/reconciliation-issues')}
                style={{
                  ...navItem,
                  ...(location.pathname === '/reconciliation-issues' ? navItemActive : {})
                }}
              >
                <span className="layout-nav-icon" style={navIcon}>🔍</span>
                Reconciliation Issues
              </Link>
            )}
            <Link 
              to="/accounting" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/accounting')}
              style={{
                ...navItem,
                ...(location.pathname === '/accounting' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📈</span>
              Accounting
            </Link>
            <Link 
              to="/amazon-tax" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/amazon-tax')}
              style={{
                ...navItem,
                ...(location.pathname === '/amazon-tax' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>💼</span>
              Amazon Tax
            </Link>
            <Link
              to="/marketplace-settlements"
              className={SIDEBAR_LINK_CLASS(location.pathname === '/marketplace-settlements')}
              style={{
                ...navItem,
                ...(location.pathname === '/marketplace-settlements' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>🧾</span>
              Marketplace Settlements
            </Link>
            {!user?.is_sub_user && (
              <Link
                to="/activity"
                className={SIDEBAR_LINK_CLASS(location.pathname === '/activity')}
                style={{
                  ...navItem,
                  ...(location.pathname === '/activity' ? navItemActive : {})
                }}
              >
                <span className="layout-nav-icon" style={navIcon}>🕘</span>
                Activity Log
              </Link>
            )}
          </div>

          <div className="layout-nav-group" style={navGroup}>
            <div className="layout-nav-group-title" style={navGroupTitle}>GST Management</div>
            <Link 
              to="/gst" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/gst')}
              style={{
                ...navItem,
                ...(location.pathname === '/gst' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📊</span>
              GST Management
            </Link>
          </div>

          <div className="layout-nav-group" style={navGroup}>
            <div className="layout-nav-group-title" style={navGroupTitle}>IRM</div>
            <Link 
              to="/edpms-reconciliation" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/edpms-reconciliation')}
              style={{
                ...navItem,
                ...(location.pathname === '/edpms-reconciliation' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📑</span>
              EDPMS Reconciliation
            </Link>
            <Link 
              to="/irm" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/irm')}
              style={{
                ...navItem,
                ...(location.pathname === '/irm' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>💰</span>
              IRM Management
            </Link>
            <Link 
              to="/irm-utilized" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/irm-utilized')}
              style={{
                ...navItem,
                ...(location.pathname === '/irm-utilized' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📊</span>
              IRM Utilized
            </Link>
            <Link 
              to="/ebrc" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/ebrc')}
              style={{
                ...navItem,
                ...(location.pathname === '/ebrc' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>📋</span>
              EBRC
            </Link>
          </div>
          
          <div className="layout-nav-group" style={navGroup}>
            <div className="layout-nav-group-title" style={navGroupTitle}>Settings</div>
            <Link 
              to="/address-book" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/address-book')}
              style={{
                ...navItem,
                ...(location.pathname === '/address-book' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>👥</span>
              Address Book
            </Link>
            <Link 
              to="/banks" 
              className={SIDEBAR_LINK_CLASS(location.pathname === '/banks')}
              style={{
                ...navItem,
                ...(location.pathname === '/banks' ? navItemActive : {})
              }}
            >
              <span className="layout-nav-icon" style={navIcon}>🏦</span>
              Bank Maintenance
            </Link>
          </div>

          {(isAdmin || isSupport) && (
            <div className="layout-nav-group" style={navGroup}>
              <div className="layout-nav-group-title" style={navGroupTitle}>Administration</div>
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={`${SIDEBAR_LINK_CLASS(location.pathname === '/admin')} layout-nav-item--admin`}
                  style={{
                    ...navItem,
                    ...(location.pathname === '/admin' ? navItemActive : {}),
                    color: '#fbbf24'
                  }}
                >
                  <span className="layout-nav-icon" style={navIcon}>⚙️</span>
                  Admin Dashboard
                </Link>
              )}
              {(isAdmin || isSupport) && (
                <Link 
                  to="/support" 
                  className={`${SIDEBAR_LINK_CLASS(location.pathname === '/support')} layout-nav-item--support`}
                  style={{
                    ...navItem,
                    ...(location.pathname === '/support' ? navItemActive : {}),
                    color: '#10b981'
                  }}
                >
                  <span className="layout-nav-icon" style={navIcon}>🎧</span>
                  Support Tickets
                </Link>
              )}
            </div>
          )}

        </nav>
      </aside>

      {/* Main Content Area – scrollable. Docked sidebar pushes content on
          desktop; drawer overlays content on narrow viewports. */}
      <main
        style={{
          ...mainContent,
          marginLeft: !isNarrow && sidebarOpen ? "280px" : 0,
          padding: isNarrow ? "16px" : "24px",
          transition: "margin-left 0.25s ease",
        }}
      >
        {children}
      </main>
      </div>
    </div>
  );
}

// Layout Styles
const dashboardLayout = { 
  minHeight: "100vh",
  background: "#f8fafc"
};

const mainRow = {
  display: "flex",
  marginTop: 48,
  minHeight: "calc(100vh - 48px)"
};

const topBar = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: 48,
  minHeight: 48,
  background: "#1e293b",
  borderBottom: "1px solid #334155",
  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  gap: 8,
  zIndex: 100,
  paddingTop: "env(safe-area-inset-top, 0px)",
  paddingLeft: "max(16px, env(safe-area-inset-left, 0px))",
  paddingRight: "max(16px, env(safe-area-inset-right, 0px))",
};

const sidebarBackdrop = {
  position: "fixed",
  top: 48,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15,23,42,0.45)",
  zIndex: 100,
  cursor: "pointer",
};

const topBarLeft = {
  display: "flex",
  alignItems: "center",
  gap: 12
};

const topBarDivider = {
  width: 1,
  height: 24,
  minWidth: 1,
  background: "rgba(255,255,255,0.25)",
  margin: "0 4px"
};

const topBarGSTWrap = {
  display: "flex",
  alignItems: "center",
  flex: 1,
  minWidth: 0,
  justifyContent: "flex-start",
};

const topBarMenuBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  padding: 0,
  border: "none",
  background: "transparent",
  borderRadius: 8,
  cursor: "pointer",
  transition: "background 0.2s"
};

const topBarLogo = {
  color: "#f8fafc",
  fontSize: 20,
  fontWeight: 700,
  textDecoration: "none",
  transition: "opacity 0.2s"
};

const topBarRight = {
  display: "flex",
  alignItems: "center",
  gap: 16
};

const topBarExtensionLink = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px",
  borderRadius: 8,
  textDecoration: "none",
  color: "#e2e8f0",
  fontSize: 14,
  fontWeight: 500,
  transition: "background 0.2s"
};

const topBarExtensionDot = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  flexShrink: 0
};

const topBarExtensionLabel = {
  whiteSpace: "nowrap"
};

const topBarProfileBtn = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  border: "none",
  background: "#ffffff",
  color: "#1e293b",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.2s"
};

const topBarProfileIcon = {};

const profileDropdown = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 8,
  minWidth: 160,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
  padding: "8px 0",
  zIndex: 200
};

const profileDropdownItem = {
  display: "block",
  width: "100%",
  padding: "10px 16px",
  border: "none",
  background: "none",
  fontSize: 14,
  fontWeight: 500,
  color: "#374151",
  textAlign: "left",
  cursor: "pointer",
  transition: "background 0.15s"
};

const sideMenu = {
  position: "fixed",
  top: 48,
  left: 0,
  bottom: 0,
  width: "280px",
  background: "#1e293b",
  color: "#f1f5f9",
  padding: "20px 0",
  boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
  overflowY: "auto",
  zIndex: 90
};

const mainContent = {
  flex: "1",
  marginLeft: "280px",
  padding: "24px",
  overflowY: "auto",
  minWidth: 0,
  minHeight: "calc(100vh - 48px)"
};

// Side Menu Styles
const navSection = {
  padding: "24px 0"
};

const navGroup = {
  marginBottom: "32px"
};

const navGroupTitle = {
  padding: "0 24px 12px",
  fontSize: "12px",
  fontWeight: "600",
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.5px"
};

const navItem = {
  display: "flex",
  alignItems: "center",
  padding: "12px 24px",
  color: "#cbd5e1",
  textDecoration: "none",
  fontSize: "14px",
  fontWeight: "500",
  transition: "all 0.2s ease",
  cursor: "pointer"
};

const navItemActive = {
  background: "#334155",
  color: "#f8fafc",
  borderRight: "3px solid #3b82f6"
};

const navIcon = {
  marginRight: "12px",
  fontSize: "16px"
};
